import { supabase } from './supabase';

export const trackLearningEvent = async ({
  userId,
  matterId = null,
  questionKey = null,
  topicId = null,
  resourceId = null,
  eventType,
  metadata = {}
}) => {
  if (!userId || !eventType) return;

  await supabase.from('education_learning_events').insert({
    user_id: userId,
    matter_id: matterId || null,
    question_key: questionKey || null,
    topic_id: topicId || null,
    resource_id: resourceId || null,
    event_type: eventType,
    metadata_json: metadata
  });
};

export const recordTopicFeedback = async ({
  userId,
  matterId = null,
  topic,
  questionKey = null,
  feedbackType,
  eventType,
  source
}) => {
  if (!userId || !topic?.id || !feedbackType || !eventType) return;

  await trackLearningEvent({
    userId,
    matterId,
    questionKey,
    topicId: topic.id,
    eventType,
    metadata: {
      source,
      topic_slug: topic.slug
    }
  });

  await supabase.from('education_topic_feedback').upsert({
    user_id: userId,
    matter_id: matterId || null,
    topic_id: topic.id,
    feedback_type: feedbackType
  }, {
    onConflict: 'user_id,matter_id,topic_id,feedback_type'
  });
};

export const removeTopicFeedback = async ({
  userId,
  matterId = null,
  topic,
  feedbackType
}) => {
  if (!userId || !topic?.id || !feedbackType) return;

  let query = supabase
    .from('education_topic_feedback')
    .delete()
    .eq('user_id', userId)
    .eq('topic_id', topic.id)
    .eq('feedback_type', feedbackType);

  query = applyMatterFilter(query, matterId);
  const { error } = await query;
  if (error) throw error;
};

const applyMatterFilter = (query, matterId) => {
  return matterId ? query.eq('matter_id', matterId) : query.is('matter_id', null);
};

export const fetchTopicFeedbackTypes = async ({ userId, matterId = null, topicId }) => {
  if (!userId || !topicId) return [];

  let query = supabase
    .from('education_topic_feedback')
    .select('feedback_type')
    .eq('user_id', userId)
    .eq('topic_id', topicId);

  query = applyMatterFilter(query, matterId);
  const { data, error } = await query;
  if (error) throw error;

  return Array.from(new Set((data || []).map((row) => row.feedback_type).filter(Boolean)));
};

export const fetchTopicFeedbackMap = async ({ userId, matterId = null, topicIds = [] }) => {
  if (!userId || topicIds.length === 0) return {};

  let query = supabase
    .from('education_topic_feedback')
    .select('topic_id, feedback_type')
    .eq('user_id', userId)
    .in('topic_id', topicIds);

  query = applyMatterFilter(query, matterId);
  const { data, error } = await query;
  if (error) throw error;

  return (data || []).reduce((map, row) => {
    if (!row.topic_id || !row.feedback_type) return map;
    map[row.topic_id] = [...(map[row.topic_id] || []), row.feedback_type];
    return map;
  }, {});
};

export const hasLearningEvent = async ({
  userId,
  matterId = null,
  topicId = null,
  resourceId = null,
  eventType
}) => {
  if (!userId || !eventType) return false;

  let query = supabase
    .from('education_learning_events')
    .select('id')
    .eq('user_id', userId)
    .eq('event_type', eventType)
    .limit(1);

  query = applyMatterFilter(query, matterId);
  if (topicId) query = query.eq('topic_id', topicId);
  if (resourceId) query = query.eq('resource_id', resourceId);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).length > 0;
};
